import { useEffect, useState } from "react";
import { HandCoins, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateLocale } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FeeStatusBadge, eurCents } from "@/components/FeesPanel";

// Le membre déclare avoir payé par virement → « en attente » ; l'admin valide à réception
export async function claimFeePaid(feeId) {
  await api.post(`/fees/${feeId}/claim-paid`);
}

// RIB du groupe, visible par les membres pour payer par virement (copie en 1 clic)
export function FeeRib({ rib }) {
  const { t } = useAuth();
  const [copied, setCopied] = useState(null);
  if (!rib?.iban) return null;
  const copy = async (val, field) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(field);
      toast.success(t("fees.copied"));
      setTimeout(() => setCopied(null), 2000);
    } catch { /* presse-papiers indisponible : le texte reste sélectionnable */ }
  };
  const Row = ({ label, value, field, mono }) => (value ? (
    <div className="flex items-center justify-between gap-2 py-1">
      <span className="text-xs t-secondary shrink-0">{label}</span>
      <span className={`text-xs font-medium truncate ${mono ? "font-mono tracking-wide" : ""}`}>{value}</span>
      <button onClick={() => copy(value, field)} className="p-1.5 rounded-lg border border-app t-secondary hover:text-white hover:bg-white/5 shrink-0" data-testid={`rib-copy-${field}`} title={t("fees.copy")} aria-label={t("fees.copy")}>
        {copied === field ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  ) : null);
  return (
    <div className="rounded-xl border border-app bg-white/[0.03] px-3 py-2" data-testid="fee-rib">
      <p className="text-[10px] uppercase tracking-wider t-secondary pb-1">{t("fees.rib_title")}</p>
      <Row label={t("fees.rib_holder")} value={rib.holder} field="holder" />
      <Row label={t("fees.rib_iban")} value={rib.iban} field="iban" mono />
      <Row label={t("fees.rib_bic")} value={rib.bic} field="bic" mono />
    </div>
  );
}

// Bannière tableau de bord : visible tant que la cotisation du membre n'est pas réglée
export function FeeDueBanner() {
  const { t, lang } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/fees/my").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data?.enabled) return null;
  const current = (data.fees || []).find((f) => f.period_id === data.current_period_id);
  if (!current || !["due", "pending"].includes(current.status)) return null;
  const rib = { holder: data.rib_holder, iban: data.rib_iban, bic: data.rib_bic };
  const claim = async () => {
    try {
      await claimFeePaid(current.id);
      toast.success(t("myfees.claim_done"));
      load();
    } catch (e) { toast.error(e.friendly); }
  };
  return (
    <section className="app-card p-4 lg:p-5 border-amber-500/40 anim-fade-up-1" data-testid="fee-due-banner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-heading text-base font-bold flex items-center gap-2">
            <HandCoins className="h-4 w-4 accent" /> {t("myfees.banner_title")}
          </h3>
          <p className="text-sm mt-1">
            <span className="font-semibold">{eurCents(current.amount_due_cents)}</span>
            <span className="t-secondary"> — {current.period_name}</span>
          </p>
          {current.period_due_date && current.status === "due" && (
            <p className="text-xs t-secondary mt-0.5">{t("myfees.due_date", { date: new Date(current.period_due_date + "T00:00").toLocaleDateString(dateLocale(lang)) })}</p>
          )}
          {current.status === "pending" && <p className="text-xs accent mt-0.5">{t("myfees.pending_hint")}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <FeeStatusBadge status={current.status} />
          {current.status === "due" && (
            <Button size="sm" className="brand-bg text-white" onClick={claim} data-testid="fee-claim-paid-banner">{t("myfees.claim_paid")}</Button>
          )}
        </div>
      </div>
      {current.status === "due" && <div className="mt-3"><FeeRib rib={rib} /></div>}
    </section>
  );
}

// Carte « Ma cotisation » du membre (Paramètres > Profil) — chaque membre ne voit que la sienne
export default function MyFeesCard() {
  const { t, lang } = useAuth();
  const [data, setData] = useState(null);

  const load = () => api.get("/fees/my").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data?.enabled) return null;
  const current = (data.fees || []).find((f) => f.period_id === data.current_period_id);
  const history = (data.fees || []).filter((f) => f.period_id !== data.current_period_id);
  const rib = { holder: data.rib_holder, iban: data.rib_iban, bic: data.rib_bic };

  const claim = async () => {
    try {
      await claimFeePaid(current.id);
      toast.success(t("myfees.claim_done"));
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  return (
    <div className="app-card p-6 space-y-4 max-w-lg" data-testid="my-fees-card">
      <h3 className="font-heading text-lg font-bold flex items-center gap-2"><HandCoins className="h-5 w-5 accent" /> {t("myfees.title")}</h3>
      {!current && <p className="text-sm t-secondary">{t("myfees.none")}</p>}
      {current && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-medium">{current.period_name}</p>
              <p className="font-heading text-2xl font-bold">{eurCents(current.amount_due_cents)}</p>
              {current.period_due_date && ["due", "pending"].includes(current.status) && (
                <p className="text-xs t-secondary">{t("myfees.due_date", { date: new Date(current.period_due_date + "T00:00").toLocaleDateString(dateLocale(lang)) })}</p>
              )}
              {current.status === "paid" && current.paid_at && (
                <p className="text-xs t-secondary">{t("myfees.paid_on", { date: new Date(current.paid_at).toLocaleDateString(dateLocale(lang)), method: t(`fees.method_${current.payment_method}`) })}</p>
              )}
            </div>
            <FeeStatusBadge status={current.status} />
          </div>
          {current.status === "due" && current.amount_due_cents > 0 && (
            <div className="space-y-3">
              <FeeRib rib={rib} />
              <Button size="sm" className="brand-bg text-white" onClick={claim} data-testid="fee-claim-paid-button">{t("myfees.claim_paid")}</Button>
            </div>
          )}
          {current.status === "pending" && <p className="text-xs accent">{t("myfees.pending_hint")}</p>}
        </div>
      )}
      {history.length > 0 && (
        <div className="border-t border-app pt-3 space-y-2">
          <p className="text-xs uppercase tracking-wider t-secondary">{t("myfees.history")}</p>
          {history.map((f) => (
            <div key={f.id} className="flex items-center justify-between text-sm" data-testid={`my-fee-history-${f.id}`}>
              <span>{f.period_name}</span>
              <FeeStatusBadge status={f.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
